import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { RestApiError, restClient } from "../../../../lib/rest-client";

type ContractItem = {
  id: string;
  talentId: string;
  clientCompany: string | null;
  projectName: string | null;
  contractType: string;
  reward: number | null;
  margin: number | null;
  startDate: string;
  endDate: string | null;
  status: string;
  renewalCount: number;
  createdAt: string;
};

type TalentWithContracts = {
  id: string;
  name: string;
  contracts: ContractItem[];
};

type ContractRow = ContractItem & {
  talentName: string;
  daysRemaining: number | null;
};

/**
 * 残日数を計算する
 * @param {string | null} endDate 契約終了日
 * @return {number | null} 残日数（nullは終了日未設定）
 */
function calcDaysRemaining(endDate: string | null): number | null {
  if (!endDate) return null;
  const diff = new Date(endDate).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

/**
 * 残日数に応じたバッジクラスを返す
 * @param {number | null} days 残日数
 * @return {string} バッジCSSクラス
 */
function getDaysRemainingClass(days: number | null): string {
  if (days === null) return "text-base-content/60";
  if (days <= 14) return "text-error font-bold";
  if (days <= 30) return "text-warning font-bold";
  return "text-success";
}

/**
 * 契約ステータスのラベルを返す
 * @param {string} status ステータス値
 * @return {object} label, badgeClass
 */
function getContractStatusBadge(status: string): {
  label: string;
  badgeClass: string;
} {
  switch (status) {
    case "active":
      return { label: "稼働中", badgeClass: "badge-success" };
    case "renewal_pending":
      return { label: "更新待ち", badgeClass: "badge-warning" };
    case "ended":
      return { label: "終了", badgeClass: "badge-ghost" };
    case "renewed":
      return { label: "更新済", badgeClass: "badge-info" };
    default:
      return { label: status, badgeClass: "badge-ghost" };
  }
}

/**
 * 契約タブコンポーネント
 * 全タレント横断の契約一覧を表示する
 * @return {React.ReactElement} 契約タブ
 */
export const ContractTab: React.FC = () => {
  const [contracts, setContracts] = useState<ContractRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("");

  const fetchContracts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // タレント一覧を取得（contractsを含む）
      const talentsRes = await restClient.get<{
        items: TalentWithContracts[];
      }>("/talents", { limit: "200" });

      // 全タレントの契約をフラットに展開
      const rows: ContractRow[] = [];
      for (const talent of talentsRes.items) {
        if (!talent.contracts || talent.contracts.length === 0) continue;
        for (const contract of talent.contracts) {
          rows.push({
            ...contract,
            talentName: talent.name,
            daysRemaining: calcDaysRemaining(contract.endDate),
          });
        }
      }

      // 残日数の少ない順にソート（nullは末尾）
      rows.sort((a, b) => {
        if (a.daysRemaining === null && b.daysRemaining === null) return 0;
        if (a.daysRemaining === null) return 1;
        if (b.daysRemaining === null) return -1;
        return a.daysRemaining - b.daysRemaining;
      });

      setContracts(rows);
    } catch (e) {
      const msg =
        e instanceof RestApiError
          ? e.message
          : "契約データの取得に失敗しました";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContracts().catch(() => undefined);
  }, [fetchContracts]);

  const filteredContracts = statusFilter
    ? contracts.filter(c => c.status === statusFilter)
    : contracts;

  if (error) {
    return (
      <div className="alert alert-error">
        <span>{error}</span>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  return (
    <div>
      {/* Filter */}
      <div className="flex items-center gap-3 mb-4">
        <select
          className="select select-bordered select-sm"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
        >
          <option value="">全ステータス</option>
          <option value="active">稼働中</option>
          <option value="renewal_pending">更新待ち</option>
          <option value="ended">終了</option>
          <option value="renewed">更新済</option>
        </select>
        <span className="text-sm text-base-content/60">
          {filteredContracts.length}件
        </span>
      </div>

      {/* Table */}
      <div className="card bg-base-100 shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>タレント</th>
                <th>契約先</th>
                <th>案件名</th>
                <th>単価</th>
                <th>マージン</th>
                <th>契約期間</th>
                <th>残日数</th>
                <th>ステータス</th>
                <th>更新回数</th>
              </tr>
            </thead>
            <tbody>
              {filteredContracts.map(contract => {
                const statusBadge = getContractStatusBadge(contract.status);
                const daysClass = getDaysRemainingClass(contract.daysRemaining);
                return (
                  <tr key={contract.id} className="hover">
                    <td className="font-medium">{contract.talentName}</td>
                    <td>{contract.clientCompany || "-"}</td>
                    <td>{contract.projectName || "-"}</td>
                    <td>
                      {contract.reward != null ? `${contract.reward}万` : "-"}
                    </td>
                    <td>
                      {contract.margin != null ? `${contract.margin}万` : "-"}
                    </td>
                    <td className="text-xs">
                      {contract.startDate}
                      {contract.endDate ? ` ~ ${contract.endDate}` : " ~"}
                    </td>
                    <td>
                      <span className={daysClass}>
                        {contract.daysRemaining !== null
                          ? `${contract.daysRemaining}日`
                          : "-"}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`badge badge-sm ${statusBadge.badgeClass}`}
                      >
                        {statusBadge.label}
                      </span>
                    </td>
                    <td>{contract.renewalCount}</td>
                  </tr>
                );
              })}
              {filteredContracts.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    className="text-center py-8 text-base-content/60"
                  >
                    契約が見つかりません
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
